// roles
export const ROLE = {
  NONE: '',
  READ_ONLY: 'org.viewer',
  TRADER: 'org.trader',
  MOBILE_UI_TRADER: 'org.ui.mobile.trader',
  TRADING_CONFIG: 'org.trading.config',
  ADMIN: 'org.admin',
  USER_ADMIN: 'org.user.admin',
  SERVICE_ACCOUNTS_ADMIN: 'org.serviceaccounts.admin',
  SPACES_ADMIN: 'org.spaces.admin',
  CREDENTIALS_ADMIN: 'org.credentials.admin',
  DEALER: 'org.dealer',
  DEALER_VIEWER: 'org.dealer.viewer',
  DEALER_ADMIN_OPS: 'org.dealer.admin.ops',
  TRANSFERS: 'org.transfers',
  TRANSFERS_HIDDEN: 'org.transfers.hidden',
  TALOS_SUPPORT: 'talos.support',
  TALOS_ADMIN: 'talos.admin',
  TALOS_ONBOARDING_ADMIN: 'talos.onboarding.admin',
  TALOS_TEST_RUNNER: 'talos.testrunner',
  TALOS_VIEWER: 'talos.viewer',
  TALOS_CANCEL_ORDERS: 'talos.orders.cancel',
  MARKETPLACE_VIEWER: 'marketplace.viewer',
  MARKETPLACE_TRADER: 'marketplace.trader',
  MARKETPLACE_ADMIN: 'marketplace.admin',
  PORTFOLIO: 'org.portfolio',
  PORTFOLIO_BETA: 'org.portfolio.beta',
  PORTFOLIO_COMPLIANCE_ADMIN: 'org.portfolio.compliance.admin',
  ANALYTICS: 'org.analytics',
  INTEL: 'org.intel',
  TREASURY: 'org.treasury',
  TREASURY_ADMIN: 'org.treasury.admin',
  SECMASTER: 'org.secmaster',
  MARKETPLACE_OPS: 'marketplace.ops',
  TRADING_CONTROLS_ADMIN: 'org.trading.controls.admin',
  PORTFOLIO_ENGINEERING: 'org.portfolio.engineering',
  /** Grants Portfolio Construction / Data Management (Rebalance, Models, etc.) */
  REBALANCE_EDITOR: 'org.portfolio.advanced.rebalance.editor',
  /** Grants Operations Compliance pages (Approvals & Policies, Universes) */
  OPERATIONS_EDITOR: 'org.portfolio.advanced.operations.editor',
  /** Grants Research tools and data-management pages */
  RESEARCH_EDITOR: 'org.portfolio.advanced.research.editor',
  AUTOHEDGING_VIEW: 'org.autohedger.view',
  AUTOHEDGING_ADMIN: 'org.autohedger.admin',
  DEALER_CONFIGURATION: 'org.dealer.configuration',
  BASKET_EDITOR: 'org.basketeditor',
  TRADER_ADMIN: 'org.trader.admin',
  LP_SETTLEMENT: 'org.lp.settlement',
} as const;
export type ROLE = (typeof ROLE)[keyof typeof ROLE];

// actions
export const ACTION = {
  VIEW_ORDER_FORM: 'order-form.view',
  VIEW_RFQ_FORM: 'rfq-form.view',
  VIEW_SALES_ORDER_FORM: 'sales-order-form.view',
  VIEW_SALES_RFQ_FORM: 'sales-rfq-form.view',
  VIEW_ORDER: 'order.view',
  VIEW_AUTOHEDGING: 'autohedging.view',
  EDIT_AUTOHEDGING: 'autohedging.edit',
  EDIT_BASKET: 'basket.edit',
  SUBMIT_ORDER: 'order.submit',
  SUBMIT_ORDER_MOBILE: 'order.submit.mobile',
  PAUSE_ORDER: 'order.pause',
  RESUME_ORDER: 'order.resume',
  CANCEL_ORDER: 'order.cancel',
  ARCHIVE_ORDER: 'order.archive',
  REQUEST_RFQ: 'rfq.request',
  REQUEST_RFQ_MOBILE: 'rfq.request.mobile',
  ACCEPT_RFQ: 'rfq.accept',
  ACCEPT_RFQ_MOBILE: 'rfq.accept.mobile',
  EDIT_TRADE: 'trade.edit',
  EDIT_MARKET_SETTINGS: 'settings.markets.edit',
  EDIT_AGGREGATION_SETTINGS: 'settings.aggregations.edit',
  EDIT_SUB_ACCOUNTS: 'settings.subaccounts.edit',
  CLONE_SUB_ACCOUNTS: 'settings.subaccounts.clone',
  EDIT_SECMASTER: 'settings.secmaster.edit',
  EDIT_SECMASTER_ADVANCED: 'settings.secmaster.editadvanced',
  SUBMIT_TRANSFER: 'transfer.submit',
  EDIT_LIMITS: 'limits.edit',
  EDIT_MAX_ORDER_SIZE_LIMITS: 'limits.max-order-size.edit',
  EDIT_COUNTERPARTY_LIMITS: 'limits.counterparty.edit',
  TOGGLE_KILLSWITCH: 'killswitch.toggle',
  EDIT_BALANCE: 'balance.edit',
  VIEW_DEALER: 'dealer.view',
  APPROVE_PENDING_TRANSACTION: 'dealer.approve.transaction',
  APPROVE_PENDING_TRANSFER: 'dealer.approve.transfer',
  FORCE_WITHDRAWAL: 'dealer.force-withdrawal',
  DEALER_TRADING: 'dealer.trading',
  EDIT_CUSTOMER_FIX_CONNECTIONS: 'dealer.fixconnections.edit',
  EDIT_DEALER: 'dealer.edit',
  EDIT_EXECUTION_RULES_MODE: 'dealer.edit.execution-rules-mode',
  EDIT_CUSTOMER_BALANCE: 'dealer.edit.customer-balance',
  EDIT_DEALER_TRADING: 'dealer.edit.trading',
  EDIT_POSITION: 'position.edit',
  VIEW_ORDER_ANALYTICS: 'orderanalytics.view',
  VIEW_PORTFOLIO: 'portfolio.view',
  VIEW_PMS_LAYOUT_SETTING: 'settings.pms-layout.view',
  /** Research tools and data-management pages */
  EDIT_RESEARCH: 'research.edit',
  /** Portfolio Construction + Data Management under Portfolio (PE tools) */
  EDIT_REBALANCE: 'rebalance.edit',
  /** Operations Compliance pages */
  EDIT_OPERATIONS: 'operations.edit',
  VIEW_TALOS_ADMIN_SETTINGS: 'talos.admin.view',
  EDIT_FILTER_PERMISSIONS: 'settings.filterpermissions.read',
  /** Market-account and ABOR permission-filter writes. Excludes User Admin; the server rejects that role. */
  EDIT_MARKET_ACCOUNT_FILTER_PERMISSIONS: 'settings.filterpermissions.marketaccounts.edit',
  EDIT_ROLLUPS: 'settings.rollups.edit',
  EDIT_MARKET_SESSION_LIMIT: 'settings.users.edit.market-session-limit',
  EDIT_MARKET_CREDENTIALS: 'settings.users.edit.market-credentials',
  // VIEW_ADDRESSES is known to product as a view-only perm
  // - TODO: Add EDIT permissions to restrict the edit functionality
  VIEW_ADDRESSES: 'settings.addresses.view',
  MANUAL_ORDER_FILL: 'manual.order.fill',
  MANUAL_RFQ_PRICING: 'manual.rfq.pricing',
  EDIT_USERS: 'settings.users.edit',
  /** View the Service Accounts blotter in Admin */
  VIEW_SERVICE_ACCOUNTS: 'settings.service-accounts.view',
  /** Create, edit and delete Service Accounts, including their API keys */
  EDIT_SERVICE_ACCOUNTS: 'settings.service-accounts.edit',
  VIEW_USER_GROUPS: 'settings.user-groups.view',
  EDIT_USERS_DEFAULT_SPACE: 'settings.users.edit.default-space',
  EDIT_NEW_USERS_DEFAULT_SPACE: 'settings.users.edit.new-users-space',
  /** List all layout specs/templates (all users) */
  LIST_ALL_SPACES: 'settings.spaces.list-all',
  /** Edit and delete any user's layout specs/templates */
  EDIT_ALL_SPACES: 'settings.spaces.edit-all',
  VIEW_USERS_HISTORY: 'settings.users.history.read',
  HEDGER_SYNCHRONIZATION: 'hedger.synchronization',
  VIEW_LEDGER_EVENTS: 'ledger.events.view',
  VIEW_CUSTOMER_CONFIGURATIONS: 'dealer.configurations.view',
  EDIT_CUSTOMER_CONFIGURATIONS: 'dealer.configurations.edit',
  EDIT_CUSTOMER_USERS: 'dealer.customers.users.edit',
  VIEW_CUSTOMER_PRICING_TIERS: 'dealer.pricing-tiers.view',
  VIEW_CUSTOMER_SYMBOL_GROUPS: 'dealer.symbol-groups.view',
  VIEW_CUSTOMER_ACQUISITION_LIMITS: 'dealer.acquisition-limits.view',
  EDIT_CUSTOMER_ACQUISITION_LIMITS: 'dealer.acquisition-limits.edit',
  VIEW_UNIFIED_LIQUIDITY_TOKENS: 'settings.view.unified-liquidity-tokens',
  EDIT_UNIFIED_LIQUIDITY_TOKENS: 'settings.edit.unified-liquidity-tokens',

  VIEW_MONITORING_ORDER_CONTEXT: 'monitoring.ordercontext.view',
  /**
   * Bypass filter permissions and be allowed to trade on all subaccounts
   */
  VIEW_FILTER_PERMISSIONS_FEATURE_DISABLED: 'settings.filterpermissions.viewfeaturedisabled',
  VIEW_ANALYTICS: 'analytics.view',
  VIEW_INTEL: 'intel.view',
  /**
   * View the ApiFilterPermissions even if the flag `'trading.EnableAccountSegregation'` is disabled
   */
  BYPASS_FILTER_PERMISSIONS_RESTRICTIONS_SUBACCOUNTS: 'principal.filterpermissions.bypass.subaccounts',

  // Talos-specific permissions
  FORCE_CANCEL_ORDER: 'order.force-cancel',
  FORCE_CANCEL_CARE_ORDER: 'care-order.force-cancel',
  FORCE_CANCEL_SPREADTOFIXING_ORDER: 'order.force-cancel-spread-to-fixing',
  FORCE_FILL_ORDER: 'order.force-fill',
  USE_ORGANIZATION: 'organization.use',
  EDIT_CONFIGURATIONS: 'settings.configurations.edit',
  EDIT_ORGANIZATIONS: 'organizations.edit',
  EDIT_MARKET_ACCOUNTS: 'market-accounts.edit',
  EDIT_MARKET_CONNECTIONS: 'market-connections.edit',
  EDIT_PROCESSES: 'processes.edit',
  MORPH_USER: 'user.morph',

  // Credentials specific
  VIEW_CREDENTIALS: 'settings.credentials.view',
  EDIT_CREDENTIALS: 'settings.credentials.edit',

  // Trading Controls
  VIEW_TRADING_CONTROLS: 'settings.trading-controls.view',
  VIEW_ALL_MARKET_ACCOUNTS_IN_TRADING_CONTROLS: 'settings.trading-controls.view.all.market.accounts',
  REQUEST_EQUITY_MARGIN_RATE_LIMIT: 'equity-margin-ratio-limit.request',
  VIEW_TRADING_CONTROLS_DIALOG: 'trading-controls-dialog.view',

  // Portfolio Engineering (legacy PE role; backwards-compatible edit access)
  EDIT_PORTFOLIO_ENGINEERING: 'portfolio-engineering.edit',

  SUBMIT_CARE_ORDER: 'careOrder.submit',
  APPROVE_CARE_ORDER_COMPLIANCE: 'care-order.compliance-approval',
  EDIT_ORDER_FIXING_DETAILS: 'order.edit.fixing-details',

  EDIT_PORTFOLIO_RECON: 'portfolio.recon.edit',
  /** POST /recon/abor/comment. Admin is allowed here and not on other recon writes. */
  ADD_ABOR_RECON_COMMENT: 'portfolio.recon.abor-comment.add',

  // Account Segregation

  /** Show all market accounts regardless of MarketAccounts Read Permission */
  VIEW_ALL_MARKET_ACCOUNTS_IN_ACCOUNT_SEG: 'settings.account-segregation.view.all.market.accounts',

  /** View the Connection Status tooltip on the Nav bar */
  VIEW_CONNECTION_STATUS: 'connection-status.view',

  /** View the Security Master screen in Admin */
  VIEW_SECURITY_MASTER: 'settings.security-master.view',

  // Notifications
  EDIT_NOTIFICATIONS_ADMIN: 'settings.notifications-admin.edit',
  /** Create/edit Slack & Telegram org destinations; Talos support API paths (UI + payload). */
  EDIT_NOTIFICATIONS_DESTINATIONS_SERVICE_CONFIG: 'settings.notifications-destinations.service-config.edit',

  // Counterparties support (LP settlement, OTC)
  EDIT_COUNTERPARTIES: 'counterparties.edit',
  MANAGE_COUNTERPARTY_EXTERNAL_ID_TYPES: 'counterparties.external-id-types.manage',

  EDIT_LP_SETTLEMENT: 'org.lp.settlement.edit',
  VIEW_LP_SETTLEMENT: 'org.lp.settlement.view',

  /** View Credit and Balances blotters */
  VIEW_CREDIT_AND_BALANCES: 'credit.balances.view',

  // OTC functionality
  CREATE_CUSTOM_OTC_INSTRUMENT: 'otc.custom-instrument.create',
  SUBMIT_DEAL: 'deal.submit',
  CANCEL_DEAL: 'deal.cancel',
  /** View Product Templates under Talos Admin → Operations. */
  VIEW_PRODUCT_TEMPLATES_ADMIN: 'talosAdmin.productTemplates.view',
  /** Edit / delete Product Templates under Talos Admin → Operations. */
  EDIT_PRODUCT_TEMPLATES_ADMIN: 'talosAdmin.productTemplates.edit',

  // Trading Aggregations view permission
  VIEW_TRADING_AGGREGATIONS: 'settings.trading-aggregations.view',
} as const;
export type ACTION = (typeof ACTION)[keyof typeof ACTION];

//marketplace actions
export const MARKETPLACE_ACTION = {
  VIEW_LOAN_QUOTE_REQUEST: 'loanQuote.view',
  SUBMIT_LOAN_QUOTE_REQUEST: 'loanQuote.submit',
  SUBMIT_CLOSE_LOAN: 'loan.submitClose',
  EDIT_CREDENTIALS: 'credentials.edit',
  SUBMIT_INITIATE_TRANSFER: 'transaction.submitTransfer',
} as const;
export type MARKETPLACE_ACTION = (typeof MARKETPLACE_ACTION)[keyof typeof MARKETPLACE_ACTION];
